import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { UrlAnimation } from "../../../../../../../components/Animation/UrlAnimation.tsx";
import { Form } from "../../../../../../../components/Form/Form.tsx";
import { useForm } from "../../../../../../../components/Form/FormContext.ts";
import { Toggle } from "../../../../../../../components/Toggle/Toggle.tsx";
import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import {
  DEFAULT_UPSCALE_CREATIVITY,
  DEFAULT_UPSCALE_RESEMBLANCE,
} from "../../../../../../boardState/stores/settings/upscaleSettingsStorage.ts";
import { PanelLayout } from "../../../../../components/Layout/PanelLayout.tsx";
import { LearnMoreLink } from "../../../../../components/LearnMoreLink.tsx";
import { PanelSection } from "../../../../../components/Panel/PanelSection.tsx";
import { ActionsBar } from "../../../../../components/Panel/Settings/ActionsBar.tsx";
import { PromptSection } from "../../../../../components/Panel/Settings/PromptSection.tsx";
import { SliderSubsection } from "../../../../../components/Panel/SliderSubsection.tsx";
import { SCALE_FACTORS } from "../../../../../constants.ts";
import { useBoardUuid } from "../../../../../hooks/useBoardUuid.ts";
import { useSelectedAsset } from "../../../../../hooks/useSelectedAsset.ts";
import { scrollToEditionHistoryStore } from "../../../../../stores/scrollToEditionHistoryStore.ts";
import type { ContentDescription } from "../../../../../types.ts";
import { getErrorsFiltered } from "../../../../../utils/getErrorsFiltered.ts";
import { UPSCALE_IMAGINATION_STRENGTH_PRESETS } from "./constants.ts";
import { useUpscaleImage } from "./hooks/useUpscaleImage.ts";
import { useUpscaleSettings } from "./hooks/useUpscaleSettings.ts";

export const UpscaleSettings = () => {
  const { mutation: upscaleMutate, isLoading: isGenerationLoading } =
    useUpscaleImage({
      onSuccess: () => {
        scrollToEditionHistoryStore.triggerScrollToEditionHistory();
      },
    });

  return (
    <Form
      schema={zUpscaleSettings}
      initialValues={{
        selectedAssetUuid: "",
        prompt: "",
        isSelectedImageSucceeded: false,
      }}
      onSubmit={(values) => {
        // FIXME: make the mutation async
        upscaleMutate({
          assetUuid: values.selectedAssetUuid,
        });
        return Promise.resolve(true);
      }}
    >
      <UpscaleSettingsFormContent isGenerationLoading={isGenerationLoading} />
    </Form>
  );
};

const zUpscaleSettings = z.object({
  selectedAssetUuid: z
    .string({ required_error: "Please upload an image in the canvas" })
    .uuid(),
  prompt: z.string().min(1, { message: "Please enter a description" }),
  isSelectedImageSucceeded: z.boolean().refine((val) => val, {
    message: "Please wait for your image to be generated",
  }),
});
type UpscaleSettingsValues = z.input<typeof zUpscaleSettings>;

export const UpscaleSettingsFormContent = ({
  isGenerationLoading,
}: {
  isGenerationLoading: boolean;
}) => {
  const boardUuid = useBoardUuid();
  const { settings, setSettings } = useUpscaleSettings(boardUuid);
  const { selectedAsset } = useSelectedAsset();

  const { data: initImageDescription } = useAppQuery<ContentDescription>({
    queryKey: selectedAsset
      ? `contents/${selectedAsset.content.uuid}/description`
      : null,
    enabled: !!selectedAsset,
  });

  const { setValues, submit, useError } = useForm<UpscaleSettingsValues>();
  const missingAssetError = useError((v) => v.selectedAssetUuid);
  let imageNotSucceededError = useError((v) => v.isSelectedImageSucceeded);
  if (missingAssetError) imageNotSucceededError = undefined;

  const promptEmptyError = useError((v) => v.prompt);

  useEffect(() => {
    setValues({ selectedAssetUuid: selectedAsset?.uuid });
  }, [setValues, selectedAsset?.uuid]);

  useEffect(() => {
    setValues({ prompt: settings.prompt });
  }, [setValues, settings.prompt]);

  useEffect(() => {
    setValues({
      isSelectedImageSucceeded: selectedAsset?.content.status === "SUCCEEDED",
    });
  }, [setValues, selectedAsset?.content.status]);

  useEffect(() => {
    if (initImageDescription?.description) {
      setSettings({
        prompt: initImageDescription.description,
      });
    }
  }, [initImageDescription, setSettings]);

  const previousImageUuid = useRef<string>();
  const [isWaitingForDescription, setIsWaitingForDescription] = useState(false);

  useEffect(() => {
    // XXX: the description loading state updates differently if the user selects another image.
    // To detect if the user has changed the selected image we use a ref previousImageUuid.
    // Case where the user is still on the same image:
    if (previousImageUuid.current === selectedAsset?.content.uuid) {
      if (isWaitingForDescription && initImageDescription?.description) {
        setSettings({
          prompt: initImageDescription.description,
        });
        setIsWaitingForDescription(false);
      }
    }
    // Case where the user selects another image:
    else {
      previousImageUuid.current = selectedAsset?.content.uuid;
      if (initImageDescription?.description) {
        setSettings({
          prompt: initImageDescription.description,
        });
        setIsWaitingForDescription(false);
      } else {
        setIsWaitingForDescription(true);
      }
    }
  }, [
    isWaitingForDescription,
    initImageDescription?.description,
    selectedAsset?.content.uuid,
    setSettings,
  ]);

  return (
    <PanelLayout
      bottomChildren={
        <ActionsBar
          isLoading={isGenerationLoading}
          onPrimaryActionClick={submit}
          primaryActionButtonContent="Upscale"
          intercomArticle="quick_upscale_and_enhance_your_images"
          errors={getErrorsFiltered([
            missingAssetError,
            imageNotSucceededError,
          ])}
        />
      }
    >
      <PanelSection
        title="Scale ratio"
        rightItem={
          <Toggle
            items={SCALE_FACTORS.map((scaleFactor) => (
              <div key={scaleFactor} className="capitalize">
                {scaleFactor}x
              </div>
            ))}
            index={SCALE_FACTORS.indexOf(settings.scale_factor)}
            onIndexChange={(index) =>
              setSettings({
                scale_factor: SCALE_FACTORS[index],
              })
            }
          />
        }
      />
      <PromptSection
        sectionTitle="Image description"
        prompt={settings.prompt}
        setPrompt={(newPrompt) => {
          setSettings({
            prompt: newPrompt,
          });
        }}
        onEnterKeyDown={submit}
        placeholder="Image description will be generated after your upload"
        errors={getErrorsFiltered([promptEmptyError])}
      />
      <PanelSection title="Creativity">
        <div className="flex-col-center gap-200">
          <Toggle
            items={UPSCALE_IMAGINATION_STRENGTH_PRESETS.map((preset) => (
              <div key={preset} className="capitalize">
                {preset.toLowerCase()}
              </div>
            ))}
            index={UPSCALE_IMAGINATION_STRENGTH_PRESETS.indexOf(
              settings.imagination_strength_preset,
            )}
            onIndexChange={(index) =>
              setSettings({
                imagination_strength_preset:
                  UPSCALE_IMAGINATION_STRENGTH_PRESETS[index],
              })
            }
            className="w-full justify-between"
          />
          {settings.imagination_strength_preset === "CUSTOM" && (
            <div className="w-full flex-col gap-150">
              <SliderSubsection
                min={0}
                max={100}
                value={Math.round(settings.creativity * 100)}
                sliderName="Creativity"
                onChange={(creativity: number) =>
                  setSettings({
                    creativity: creativity / 100,
                    imagination_strength_preset: "CUSTOM",
                  })
                }
                sliderInformationSection={
                  <div className="flex-col body-md-default text-primary gap-200">
                    <div>
                      <span className="body-md-semibold">Creativity</span> -
                      Allows the AI to "hallucinate" additional details,
                      achieving greater realism at the cost of moving further
                      away from the original image. Here's the Pimento's touch!
                      But be careful: really high values can lead to some pretty
                      strange results.
                    </div>
                    <span className="body-md-semibold">
                      Recommendation:{" "}
                      {Math.round(DEFAULT_UPSCALE_CREATIVITY * 100)}%
                    </span>
                    <LearnMoreLink
                      intercomArticle="quick_upscale_and_enhance_your_images"
                      className="self-end"
                    />
                  </div>
                }
              />
              <SliderSubsection
                min={0}
                max={100}
                value={Math.round(settings.resemblance * 100)}
                sliderName="Similarity"
                onChange={(resemblance: number) =>
                  setSettings({
                    resemblance: resemblance / 100,
                    imagination_strength_preset: "CUSTOM",
                  })
                }
                sliderInformationSection={
                  <div className="min-h-[330px] text-primary body-md-default flex-col gap-200">
                    <div>
                      <span className="body-md-semibold">Similarity</span> -
                      Increasing this value will make the generation more
                      closely resemble the original image, but very high values
                      can result in blotches or a dirtier look. Lower values
                      give more freedom to the generation at the cost of moving
                      further away from the original image.
                    </div>
                    <span className="body-md-semibold">
                      Recommendation:{" "}
                      {Math.round(DEFAULT_UPSCALE_RESEMBLANCE * 100)}%
                    </span>
                    <UrlAnimation url="https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/092871bb-0f96-45bc-926d-c94a22fa46cc.json" />
                    <LearnMoreLink
                      intercomArticle="quick_upscale_and_enhance_your_images"
                      className="mr-100 self-end"
                    />
                  </div>
                }
              />
            </div>
          )}
        </div>
      </PanelSection>
    </PanelLayout>
  );
};
